import { z } from 'zod';
import { addressDocumentSchema } from './address';

// Truncates rather than rejects (unlike `.max()`), so an overlong value in
// one field of a larger object can never invalidate the whole object — see
// the `attribution` field below for why that distinction matters here.
// Array.from() splits on Unicode code points, not UTF-16 code units (unlike
// `.slice()`), so truncating at the boundary can't land mid-surrogate-pair
// and emit a lone surrogate — which is invalid UTF-8 and would fail at
// Firestore write time (FRESH EYES review, PR #1852).
const truncatedString = (max: number) =>
  z.string().transform((s) => Array.from(s).slice(0, max).join(''));

const badgeCountsSchema = z.object({
  quick_replies: z.number(),
  as_described: z.number(),
  fast_shipper: z.number(),
});

export const roleSchema = z.object({
  admin: z.boolean().optional(),
  auctions: z.boolean().optional(),
  support: z.boolean().optional(),
  experiences: z.boolean().optional(),
  blog: z.boolean().optional(),
  seo: z.boolean().optional(),
  integrations: z.boolean().optional(),
  // GB #1942: admin-set flag; messages from trusted partners skip contact-info
  // masking. Optional with NO default so getValidUserDocument's signup output
  // is unchanged. Clients cannot write it (firestore.rules blocks `roles`).
  trusted_partner: z.boolean().optional(),
});

export const metricSchema = z.object({
  first_purchase: z.number(), // timesteamp of the first item purchased
  last_purchase: z.number(), // timesteamp of the last item purchased
  ltv_purchased: z.number(), // lifetime sum of purchases in dollars
  num_purchased: z.number(),
  num_refunded: z.number(),
  ltv_sold: z.number(), // lifetime sum of sales in dollars
  num_sold: z.number(),
  first_sold: z.number(), // time stamp of the first item sold
  last_sold: z.number(), // time stamp of the last item sold
  last_listed: z.number(), // time stamp of the last item listed
  live_listings: z.number(),
  dropped_price: z.boolean(),
  offset_sales: z.number().optional(),
});

export const noteSchema = z.object({
  created: z.number(),
  value: z.string(),
  uid: z.string(),
});

// Tracks when a user initiates new conversations (for rate limiting)
export const messageInitiationSchema = z.object({
  timestamp: z.number(),
  recipient_id: z.string(),
});

const recommendedGearSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  link: z.string().optional(),
  image: z.string().optional(),
});

export const spotlightInfoSchema = z.object({
  youtube_url: z.string().optional(),
  featured_quote: z.string().optional(),
  tagline: z.string().optional(),
  recommended_gear: z.array(recommendedGearSchema).optional(),
  is_spotlight: z.boolean().default(false),
  banner_image: z.string().optional(),
});

export const userDocumentSchema = z.object({
  spotlight_info: spotlightInfoSchema.optional(),
  permissions: z.object({
    is_verified: z.boolean().default(false),
    is_banned: z.boolean().default(false),
    is_seller: z.boolean().default(false),
    is_fraudulent: z.boolean().default(false),
    is_email_verified: z.boolean().default(false),
    is_phone_verified: z.boolean().default(false),
    is_restricted: z.boolean().default(true),
    is_monitored: z.boolean().default(false),
    can_message: z.boolean().default(false),
    can_buy: z.boolean().default(false),
    can_sell: z.boolean().default(false),
    listing_expiration: z.number().optional(),
    // GearSync integration access. `enabled` gates the dashboard section as
    // a whole; per-provider flags gate connecting/managing each one.
    // Default-deny: a missing field is treated as false.
    integrations: z
      .object({
        enabled: z.boolean().default(false),
        ebay: z.boolean().default(false),
        'lightspeed-retail': z.boolean().default(false),
      })
      .optional(),
  }),
  account_activated: z.boolean().default(false), //If the user has gone through process to become a seller
  account_id: z.string().optional(), //Stripe Seller ID
  addresses: z.array(addressDocumentSchema).optional(),
  // First-touch marketing attribution, captured client-side from the
  // gf_attribution cookie at signup, for campaign-recruit tracking (GB
  // web#1732). Optional-without-default on purpose: it must not become
  // part of the default signup-parse shape (see seller_follow_popup_seen
  // note below re: the #1472 / 2026-03-26 regression class) — only set
  // when a real cookie value exists.
  //
  // Deliberately NARROWER than the full AttributionData shape used on
  // Offer/Order (offers.ts) — this is NOT the same schema, and should not
  // be made to derive from one another. /users is `allow read;`
  // (unconditional, world-readable — firestore.rules:150) and the whole
  // document is synced to Algolia's public `users` index
  // (gearfocus-api functions/src/algolia/utils/index.ts). Offers/orders
  // are not world-readable. gclid/msclkid/fbclid are ad-platform CLICK
  // IDENTIFIERS — personal data under GDPR/CCPA, and redistributing them
  // is restricted by the ad platforms' own terms — so they are excluded
  // here entirely rather than capped: this report never needs them
  // (scripts/recruit-tracking-report.js reads only utm_source/
  // utm_campaign), so there is no reason to put a new class of sensitive
  // data on a public, indexed surface. referrer/landing_page are kept —
  // page paths/domains, not identifiers, and useful for channel
  // diagnosis.
  //
  // Adversarial-CPO review (PR #1852, MEDIUM-4): an earlier version of
  // this used `.max(500)` (reject-on-overflow), matching this file's
  // usual "invalid shape is dropped whole" idiom -- but that meant one
  // oversized field (ad platforms routinely stuff utm_content/utm_term
  // with long creative/ad-set tracking strings) silently destroyed the
  // WHOLE attribution object, including a perfectly valid utm_campaign —
  // the one field this report actually filters on. TRUNCATE per field
  // instead (matching the existing precedent in
  // getAttributionForStripe(), attribution.ts:221-236, which
  // .substring(0, 500)s rather than rejects) so an overlong low-value
  // field can never take down the high-value one.
  attribution: z
    .object({
      utm_source: truncatedString(500).optional(),
      utm_medium: truncatedString(500).optional(),
      utm_campaign: truncatedString(500).optional(),
      utm_term: truncatedString(500).optional(),
      utm_content: truncatedString(500).optional(),
      referrer: truncatedString(500).optional(),
      landing_page: truncatedString(500).optional(),
      first_touch: z.number().optional(),
    })
    .optional(),
  author: z.string().optional(), // old site userId
  badge_counts: z.array(badgeCountsSchema),
  badges: z.array(z.any()),
  bio: z.string().optional(),
  brands: z.array(z.any()),
  business_type: z
    .union([z.literal('individual'), z.literal('company')])
    .optional(),
  country: z.string(),
  created: z.number(),
  deleted: z.boolean().optional(),
  email: z.string(),
  favorites: z.array(z.string()).optional(),
  first: z.string().optional(),
  following: z.array(z.string()),
  from_web: z.boolean(),
  // GB #691: set true the first time this person is shown the one-time
  // "follow this seller" popup, so it is never shown to them again on any
  // seller page. Optional-without-default on purpose: it must not become part
  // of the signup parse output (getValidUserDocument), which would change the
  // create-doc shape — the #1472 / 2026-03-26 regression class.
  seller_follow_popup_seen: z.boolean().optional(),
  hide_status: z.boolean(),
  last: z.string().optional(),
  last_active: z.number(),
  last_login: z.number().optional().nullable(),
  last_app_active: z.string().optional(),
  name: z.string(),
  on_vacation: z.boolean().default(false),
  offer_settings: z
    .object({
      default_enabled: z.boolean().default(true), // Whether new listings allow Make Offer by default
    })
    .optional(),
  opted_out: z.boolean(),
  phone: z.string(),
  photo: z.string(),
  store_photo: z.string().optional(),
  store_video: z.string().optional(),
  // GB #1612: optional phone-only banner. Below sm (640px) breakpoint this
  // takes priority over store_photo (which is center-cropped on phones).
  // MUST be added here -- userDocumentSchema is not .strict()/.passthrough(),
  // so a plain z.object() silently drops any key not listed (see api#324:
  // the same class of bug already live for sold_count). Without this line,
  // an upload would succeed, the preview would show it, the success toast
  // would fire, and the value would vanish on reload because it was never
  // actually written to Firestore.
  store_photo_mobile: z.string().optional(),
  rating: z.number(),
  reviews: z.number(),
  roles: roleSchema.optional(),
  state: z.string().optional(),
  uid: z.string(),
  username: z.string(),
  username_changed: z.number(),
  username_slug: z.string().optional(),
  customer_id: z.string().optional(),
  seller_profile: z
    .object({
      ebay_username: z.string().nullable().optional(),
      instagram: z.string().nullable().optional(),
      youtube: z.string().nullable().optional(),
      website: z.string().nullable().optional(),
      tiktok: z.string().nullable().optional(),
      linkedin: z.string().nullable().optional(),
      facebook: z.string().nullable().optional(),
      fredmiranda: z.string().nullable().optional(),
      profile_completed_at: z.number().nullable().optional(),
      // Enrichment data from verification lookups
      ebay_enrichment: z.record(z.unknown()).optional(),
      youtube_enrichment: z.record(z.unknown()).optional(),
      website_enrichment: z.record(z.unknown()).optional(),
      instagram_enrichment: z.record(z.unknown()).optional(),
      tiktok_enrichment: z.record(z.unknown()).optional(),
      linkedin_enrichment: z.record(z.unknown()).optional(),
      facebook_enrichment: z.record(z.unknown()).optional(),
      fredmiranda_enrichment: z.record(z.unknown()).optional(),
    })
    .optional(),
  shipping_policy: z.string().optional(),
  return_policy: z.string().optional(),
  tax_states: z.array(z.string()).optional(),
  metrics: metricSchema.partial().optional(),
  stripe: z.object({
    cards: z
      .array(
        z.object({
          created: z.number(),
          payment_method: z.string(),
        })
      )
      .optional(),
    customer_id: z.string().optional(),
    account_id: z.string().optional(),
    last_updated: z.number().optional(),
    payouts_enables: z.boolean().optional(),
    comapany: z.object({}).optional(),
    individual: z.object({}).optional(),
    external_accounts: z.array(z.string()).optional(),
    requirements: z
      .object({
        disabled_reason: z.string().optional(),
        eventually_due: z.array(z.string()).optional(),
        currently_due: z.array(z.string()).optional(),
        past_due: z.array(z.string()).optional(),
        errors: z
          .array(
            z.object({
              code: z.string(),
              reason: z.string(),
              requirement: z.string(),
            })
          )
          .optional(),
        pending_verification: z.array(z.string()).optional(),
      })
      .optional(),
    future_requirements: z.object({}).optional(),
  }),
  integrations: z
    .object({
      source: z.string(),
      sales_offset: z.number().optional(),
    })
    .optional(),
  notes: z.array(noteSchema).optional(),
  // Tracks new conversation initiations for rate limiting
  message_initiations: z.array(messageInitiationSchema).optional(),
  watchedModels: z.array(z.string()).optional(),
});

export const updateFavoritesArgsSchema = z.object({
  uid: z.string(),
  productId: z.string(),
  remove: z.boolean().optional(),
});

export const updateAddressesArgsSchema = z.object({
  uid: z.string(),
  address: addressDocumentSchema,
  remove: z.boolean().optional(),
});

export type Role = z.infer<typeof roleSchema>;
export type UserDocument = z.infer<typeof userDocumentSchema>;
export type BadgeCounts = z.infer<typeof badgeCountsSchema>;
export type UserAddress = z.infer<typeof addressDocumentSchema>;
export type MessageInitiation = z.infer<typeof messageInitiationSchema>;
